import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAudiencesService } from '@/flight-deck-api/services/audiences.service'
import { useOrderItemsService } from '@/flight-deck-api/services/order-items.service'
import type { AudienceComparisonResult } from './types'

export const useAudienceComparison = ({
  appliedAudienceId,
  campaignGroupId,
}: {
  appliedAudienceId: number
  campaignGroupId: number
}): {
  data: AudienceComparisonResult | undefined
  isLoading: boolean
  isError: boolean
} => {
  const { getOrderItemByCampaignGroupId } = useOrderItemsService()
  const { getAudienceComparison } = useAudiencesService()

  // Fetch order item to get original audience ID
  const {
    data: orderItem,
    isLoading: isLoadingOrderItems,
    isError: isErrorOrderItems,
  } = useQuery({
    queryKey: ['orderItem', campaignGroupId],
    // biome-ignore lint/plugin: RETIRED, do not copy. Call it through useSelectQuery/useSelectMutation when touching this.
    queryFn: () => getOrderItemByCampaignGroupId.handler({ campaignGroupId }),
    enabled: !!campaignGroupId,
  })
  const originalAudienceId = orderItem?.audienceId

  const audienceIds = useMemo(() => {
    if (!originalAudienceId) return []
    return [appliedAudienceId, originalAudienceId]
  }, [appliedAudienceId, originalAudienceId])

  const {
    data: comparison,
    isLoading: isLoadingPreviews,
    isError: isErrorPreviews,
  } = useQuery({
    queryKey: ['audienceComparison', appliedAudienceId, originalAudienceId],
    queryFn: () =>
      // biome-ignore lint/plugin: RETIRED, do not copy. Call it through useSelectQuery/useSelectMutation when touching this.
      getAudienceComparison.handler({
        audienceId: appliedAudienceId,
        campaignGroupId,
      }),
    enabled: audienceIds.length > 0, // Only run if we have valid IDs
  })

  const comparisonResult = useMemo(() => {
    // If no order item, original audience, or previews, return no alert
    if (!orderItem || !originalAudienceId || !comparison) {
      return {
        shouldShowAlert: false,
      }
    }

    const { current, original } = comparison

    // Now we know both sizes are numbers (including 0)
    const originalSize = original.availableAudienceSize
    const appliedSize = current.availableAudienceSize

    const shouldShowAlert = appliedSize < originalSize

    return {
      shouldShowAlert,
    }
  }, [orderItem, originalAudienceId, comparison])

  return {
    data: comparisonResult,
    isLoading: isLoadingOrderItems || isLoadingPreviews,
    isError: isErrorOrderItems || isErrorPreviews,
  }
}
